import React, { useLayoutEffect, useRef, useState } from 'react';
import { Pencil1Icon } from '@radix-ui/react-icons';

import { KeyboardKeys } from '../../../assets/enums/keyboard-keys.enum';

import styles from './asset-name-editor.module.scss';
const MAX_NAME_LENGTH = 64;

type AssetNameEditorProps = {
  name: string;
  onRename: (name: string) => void;
};

export const AssetNameEditor: React.FC<AssetNameEditorProps> = ({ name, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  const isCancelled = useRef(false);

  const startEditing = () => {
    setValue(name);
    isCancelled.current = false;
    setIsEditing(true);
  };

  useLayoutEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commit = () => {
    setIsEditing(false);

    if (isCancelled.current) {
      return;
    }

    const trimmed = value.trim();
    if (trimmed && trimmed !== name) {
      onRename(trimmed);
    }
  };

  const cancel = () => {
    isCancelled.current = true;
    setValue(name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyboardKeys.Enter) {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === KeyboardKeys.Escape) {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      // The wrapper's ::after mirrors the value so the input sizes to its text.
      <span className={styles['input-sizer']} data-value={value}>
        <input
          className={styles['name-input']}
          ref={inputRef}
          value={value}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          // Editing happens "in place" inside the breadcrumb crumb, so stop the
          // crumb's own click handler from firing while typing.
          onClick={(e) => e.stopPropagation()}
        />
      </span>
    );
  }

  return (
    <span className={styles['container']}>
      <span className={styles['asset-name']}>{name}</span>
      <button
        className={styles['edit-button']}
        type="button"
        aria-label="Rename asset"
        title="Rename"
        onClick={startEditing}
      >
        <Pencil1Icon />
      </button>
    </span>
  );
};
